import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
// @mui
import { styled } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Slide,
  TextField,
  Typography,
} from "@mui/material";
// utils
import { bgBlur } from "../../../utils/cssStyles";
// component
import Iconify from "../../../components/iconify";
import { useSearchGlobalQuery } from "../../../features/dashboard/dashboardApiSlice";

// ----------------------------------------------------------------------

const HEADER_MOBILE = 64;
const HEADER_DESKTOP = 92;

const StyledSearchbar = styled('div')(({ theme }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  top: 0,
  left: 0,
  zIndex: 99,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  height: HEADER_MOBILE,
  padding: theme.spacing(0, 3),
  boxShadow: theme.customShadows.z8,
  [theme.breakpoints.up('md')]: {
    height: HEADER_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

// ----------------------------------------------------------------------

const typeLabels = {
  member: "Mitglied",
  contract: "Vertrag",
  site: "Standort",
  event: "Veranstaltung",
  tournament: "Turnier",
};

export default function Searchbar() {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedQuery(inputValue.trim());
    }, 300);
    return () => clearTimeout(timeout);
  }, [inputValue]);

  const { data: results = [], isFetching } = useSearchGlobalQuery(debouncedQuery, {
    skip: !open || debouncedQuery.length < 2,
  });

  const options = useMemo(
    () => (Array.isArray(results) ? results : []),
    [results],
  );

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
    setInputValue("");
    setDebouncedQuery("");
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        {!open && (
          <IconButton onClick={handleOpen}>
            <Iconify icon="eva:search-fill" />
          </IconButton>
        )}

        <Slide direction="down" in={open} mountOnEnter unmountOnExit>
          <StyledSearchbar>
            <Autocomplete
              fullWidth
              autoHighlight
              options={options}
              loading={isFetching}
              popupIcon={null}
              filterOptions={(x) => x}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option?.id === value?.id && option?.type === value?.type
              }
              noOptionsText={
                debouncedQuery.length < 2
                  ? "Mindestens 2 Zeichen eingeben"
                  : "Keine Ergebnisse"
              }
              onChange={(_event, option) => {
                if (!option?.route) return;
                navigate(option.route);
                handleClose();
              }}
              inputValue={inputValue}
              onInputChange={(_event, value) => setInputValue(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  placeholder="Suchen..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify
                          icon="eva:search-fill"
                          sx={{
                            color: "text.disabled",
                            width: 20,
                            height: 20,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={`${option.type}-${option.id}`}>
                  <Box>
                    <Typography variant="subtitle2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {typeLabels[option.type] || option.type}
                      {option.secondary ? ` • ${option.secondary}` : ""}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </StyledSearchbar>
        </Slide>
      </div>
    </ClickAwayListener>
  );
}
